import React, { useState, useEffect, useRef } from "react";
import { toast } from "react-toastify";
import {
  CreditCard,
  Check,
  Loader2,
  AlertTriangle,
  RefreshCw,
  Banknote,
  Ban,
} from "lucide-react";

import api from "../../services/api";
import toastError from "../../errors/toastError";

/**
 * Estado del cobro automático, en las palabras que le importan al cliente.
 *
 * Existe un token guardado desde el momento en que se crea la suscripción,
 * antes de que el cliente la apruebe: decir "activo" ahí sería mentirle y
 * dejarlo esperando un cobro que nunca va a ocurrir.
 */
const RecurrenceNote = ({ status }) => {
  const normalized = String(status || "").toUpperCase();

  if (normalized === "APPROVAL_PENDING" || normalized === "APPROVED") {
    return (
      <p className="mt-1.5 text-[10px] text-amber-600 dark:text-amber-400">
        Falta aprobar la suscripción para que el cobro sea automático
      </p>
    );
  }

  if (normalized === "AMOUNT_MISMATCH") {
    return (
      <p className="mt-1.5 text-[10px] text-amber-600 dark:text-amber-400">
        El importe cambió: hay que recrear la suscripción
      </p>
    );
  }

  if (
    normalized === "CANCELLED" ||
    normalized === "SUSPENDED" ||
    normalized === "EXPIRED" ||
    normalized === "CANCEL_FAILED"
  ) {
    return (
      <p className="mt-1.5 text-[10px] text-rose-600 dark:text-rose-400">
        Cobro automático detenido ({normalized.toLowerCase()}) — hay que pagar a
        mano
      </p>
    );
  }

  return (
    <p className="mt-1.5 text-[10px] text-emerald-600">
      Cobro automático activo — no hay que hacer nada cada mes
    </p>
  );
};

/**
 * Lets the customer choose (and later change) which gateway they pay with,
 * among the ones the platform owner enabled.
 */
// Solo se ofrece dar de baja cuando hay algo realmente cobrando. Un
// CANCELLED/EXPIRED ya está apagado, y ofrecerlo otra vez confundiría.
const CANCELLABLE_STATES = ["ACTIVE", "APPROVAL_PENDING", "APPROVED", "AMOUNT_MISMATCH"];

const PaymentMethodPicker = ({ canPay, onChanged }) => {
  const isMounted = useRef(true);
  const [data, setData] = useState(null);
  const [saving, setSaving] = useState(null);
  const [cancelling, setCancelling] = useState(false);
  const [confirmingCancel, setConfirmingCancel] = useState(false);

  useEffect(() => () => { isMounted.current = false; }, []);

  const load = async () => {
    try {
      const res = await api.get("/billing/payment-methods");
      if (isMounted.current) setData(res.data);
    } catch (err) {
      toastError(err);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const handleSelect = async (method) => {
    if (!method.available || !canPay) return;
    if (data?.selected === method.id) return;

    setSaving(method.id);
    try {
      await api.put("/billing/payment-method", { gateway: method.id });
      toast.success(`Método de pago cambiado a ${method.label}`);
      await load();
      if (onChanged) onChanged(method.id);
    } catch (err) {
      toastError(err);
    } finally {
      if (isMounted.current) setSaving(null);
    }
  };

  /**
   * Da de baja el cobro automático. El backend distingue tres desenlaces y hay
   * que mostrarlos distinto: si la pasarela rechazó la baja, decirle "cancelado"
   * al cliente sería mentirle sobre su propia plata.
   */
  const handleCancelSubscription = async () => {
    setCancelling(true);
    try {
      const { data: result } = await api.post("/billing/subscription/cancel");

      if (result.cancelled) toast.success(result.message);
      else if (result.needsAttention) toast.error(result.message);
      else toast.info(result.message);

      setConfirmingCancel(false);
      await load();
      if (onChanged) onChanged();
    } catch (err) {
      toastError(err);
    } finally {
      if (isMounted.current) setCancelling(false);
    }
  };

  if (!data) return null;

  const effective = data.selected || data.fallback;

  // ¿Hay un cobro automático vivo en el método que se está usando?
  const activeMethod = (data.methods || []).find(
    method => method.id === (data.selected || data.fallback)
  );
  const cancellable =
    !!activeMethod?.hasRecurringToken &&
    CANCELLABLE_STATES.includes(
      String(activeMethod.recurringStatus || "").toUpperCase()
    );

  return (
    <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-5 mb-4">
      <div className="flex items-center gap-2 mb-1">
        <CreditCard className="w-4 h-4 text-indigo-600" />
        <h2 className="text-sm font-semibold text-slate-900 dark:text-slate-100">
          Método de pago
        </h2>
      </div>
      <p className="text-xs text-slate-500 dark:text-slate-400 mb-4">
        Elige cómo quieres pagar tus facturas de {data.currency}. Puedes cambiarlo
        cuando quieras.
      </p>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        {data.methods.map((method) => {
          const isSelected = effective === method.id;
          const disabled = !method.available || !canPay;

          return (
            <button
              key={method.id}
              onClick={() => handleSelect(method)}
              disabled={disabled || saving === method.id}
              className={`text-left p-3 rounded-xl border transition-all ${
                isSelected
                  ? "border-indigo-500 bg-indigo-50 dark:bg-indigo-950/30 ring-1 ring-indigo-500"
                  : "border-slate-200 dark:border-slate-700 hover:border-indigo-300"
              } ${disabled ? "opacity-50 cursor-not-allowed" : ""}`}
            >
              <div className="flex items-start justify-between gap-2">
                <span className="text-sm font-semibold text-slate-900 dark:text-slate-100">
                  {method.label}
                </span>
                {saving === method.id ? (
                  <Loader2 className="w-4 h-4 animate-spin text-indigo-500 flex-shrink-0" />
                ) : (
                  isSelected && (
                    <Check className="w-4 h-4 text-indigo-600 flex-shrink-0" />
                  )
                )}
              </div>

              <p className="mt-1.5 flex items-center gap-1 text-[11px] text-slate-500 dark:text-slate-400">
                {method.supportsAutoRecurring ? (
                  <>
                    <RefreshCw className="w-3 h-3" />
                    Cobro automático
                  </>
                ) : (
                  <>
                    <Banknote className="w-3 h-3" />
                    Pago manual cada mes
                  </>
                )}
              </p>

              {!method.available && method.unavailableReason && (
                <p className="mt-1.5 text-[10px] text-slate-400">
                  {method.unavailableReason}
                </p>
              )}

              {method.available && method.hasRecurringToken && (
                <RecurrenceNote status={method.recurringStatus} />
              )}

              {method.available && method.warning && (
                <p className="mt-1.5 flex items-start gap-1 text-[10px] text-amber-600 dark:text-amber-400">
                  <AlertTriangle className="w-3 h-3 mt-px flex-shrink-0" />
                  <span>{method.warning}</span>
                </p>
              )}
            </button>
          );
        })}
      </div>

      {!canPay && (
        <p className="mt-3 text-[11px] text-slate-400">
          No tienes permiso para cambiar el método de pago.
        </p>
      )}

      {data.selected && data.selected !== data.fallback && (
        <p className="mt-3 text-[11px] text-slate-400">
          Al cambiar de pasarela se cancela el cobro recurrente de la anterior para
          evitar cobros duplicados.
        </p>
      )}

      {/* Baja del cobro automático. Es plata del cliente: se pide confirmación
          explícita y se aclara qué NO hace (no condona lo que se deba). */}
      {canPay && cancellable && (
        <div className="mt-4 pt-4 border-t border-slate-200 dark:border-slate-800">
          {!confirmingCancel ? (
            <button
              type="button"
              onClick={() => setConfirmingCancel(true)}
              className="inline-flex items-center gap-1.5 text-xs font-medium text-slate-500 dark:text-slate-400 hover:text-rose-600 dark:hover:text-rose-400 transition-colors"
            >
              <Ban className="w-3.5 h-3.5" />
              Cancelar el cobro automático
            </button>
          ) : (
            <div className="rounded-lg border border-amber-200 dark:border-amber-900 bg-amber-50 dark:bg-amber-950/30 p-3">
              <p className="text-xs font-semibold text-amber-900 dark:text-amber-200">
                ¿Cancelar el cobro automático?
              </p>
              <p className="mt-1 text-[11px] text-amber-800 dark:text-amber-300">
                No se te volverá a cobrar solo. El servicio sigue activo y las
                facturas que queden pendientes se pagan a mano desde esta misma
                pantalla.
              </p>
              <div className="mt-3 flex items-center gap-2">
                <button
                  type="button"
                  onClick={handleCancelSubscription}
                  disabled={cancelling}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-rose-600 hover:bg-rose-700 text-white text-xs font-semibold disabled:opacity-50"
                >
                  {cancelling ? (
                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  ) : (
                    <Ban className="w-3.5 h-3.5" />
                  )}
                  Sí, cancelar
                </button>
                <button
                  type="button"
                  onClick={() => setConfirmingCancel(false)}
                  disabled={cancelling}
                  className="px-3 py-1.5 rounded-lg border border-amber-300 dark:border-amber-800 text-xs font-medium text-amber-900 dark:text-amber-200 disabled:opacity-50"
                >
                  No, mantenerlo
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default PaymentMethodPicker;
